import { useEffect } from 'react';

const LocalSEOPanama = ({ 
  language = 'it',
  businessType = 'legal',
  location = 'panama-city'
}: {
  language?: 'it' | 'es' | 'en';
  businessType?: 'legal' | 'consulting' | 'financial';
  location?: 'panama-city' | 'san-miguelito' | 'tocumen' | 'chorrera';
}) => {
  
  useEffect(() => {
    // Enhanced Local SEO for Panama Market
    const implementLocalSEO = () => {
      const localData = getLocalSEOData(language, businessType, location);
      
      // Update geo-specific meta tags
      updateGeoMetaTags(localData);
      
      // Update local business structured data
      updateLocalBusinessSchema(localData);
      
      // Update location-specific keywords
      updateLocationKeywords(localData);
      
      // Implement Panama-specific hreflang
      updatePanamaHreflang();
      
      // Add local business verification tags
      addBusinessVerificationTags();
    };
    
    implementLocalSEO();
  }, [language, businessType, location]);

  const getLocalSEOData = (language: string, businessType: string, location: string) => {
    const locations = {
      'panama-city': {
        name: 'Panama City',
        nameEs: 'Ciudad de Panamá',
        nameIt: 'Città di Panama',
        coordinates: { lat: '8.9824', lng: '-79.5199' },
        postalCode: '0801',
        district: 'San Felipe'
      },
      'san-miguelito': {
        name: 'San Miguelito',
        nameEs: 'San Miguelito',
        nameIt: 'San Miguelito',
        coordinates: { lat: '9.0333', lng: '-79.4667' },
        postalCode: '0902',
        district: 'San Miguelito'
      },
      'tocumen': {
        name: 'Tocumen',
        nameEs: 'Tocumen',
        nameIt: 'Tocumen',
        coordinates: { lat: '9.0667', lng: '-79.3833' },
        postalCode: '0903',
        district: 'Pedregal'
      },
      'chorrera': {
        name: 'La Chorrera',
        nameEs: 'La Chorrera',
        nameIt: 'La Chorrera',
        coordinates: { lat: '8.8833', lng: '-79.7833' },
        postalCode: '0904',
        district: 'La Chorrera'
      }
    };

    const businessTypes = {
      legal: {
        it: {
          name: 'Studio Legale Specializzato in Residenza Panama',
          description: 'Studio legale leader a Panama per servizi di residenza, cittadinanza e investimenti. Esperti in diritto internazionale e fiscalità panamense.',
          services: ['Residenza Permanente', 'Società Offshore', 'Investimenti Immobiliari', 'Consulenza Fiscale', 'Apertura Conti Bancari'],
          keywords: 'studio legale panama, avvocato panama, residenza panama, consulenza legale panama'
        },
        es: {
          name: 'Bufete Legal Especializado en Residencia Panamá',
          description: 'Bufete legal líder en Panamá para servicios de residencia, ciudadanía e inversiones. Expertos en derecho internacional y fiscalidad panameña.',
          services: ['Residencia Permanente', 'Sociedades Offshore', 'Inversiones Inmobiliarias', 'Consultoría Fiscal', 'Apertura Cuentas Bancarias'],
          keywords: 'bufete legal panama, abogado panama, residencia panama, consultoria legal panama'
        },
        en: {
          name: 'Legal Firm Specialized in Panama Residency',
          description: 'Leading legal firm in Panama for residency, citizenship and investment services. Experts in international law and Panamanian taxation.',
          services: ['Permanent Residency', 'Offshore Companies', 'Real Estate Investments', 'Tax Consulting', 'Bank Account Opening'],
          keywords: 'legal firm panama, lawyer panama, panama residency, legal consulting panama'
        }
      },
      consulting: {
        it: {
          name: 'Consulenza Specializzata per Investimenti Panama',
          description: 'Consulenza professionale per investimenti e opportunità di business a Panama. Servizi completi per imprenditori italiani.',
          services: ['Consulenza Investimenti', 'Business Setup', 'Pianificazione Fiscale', 'Due Diligence', 'Gestione Patrimoniale'],
          keywords: 'consulenza panama, investimenti panama, business panama, consulente panama'
        },
        es: {
          name: 'Consultoría Especializada para Inversiones Panamá',
          description: 'Consultoría profesional para inversiones y oportunidades de negocio en Panamá. Servicios completos para empresarios.',
          services: ['Consultoría Inversiones', 'Establecimiento Negocios', 'Planificación Fiscal', 'Due Diligence', 'Gestión Patrimonial'],
          keywords: 'consultoria panama, inversiones panama, negocios panama, consultor panama'
        },
        en: {
          name: 'Specialized Consulting for Panama Investments',
          description: 'Professional consulting for investments and business opportunities in Panama. Complete services for international entrepreneurs.',
          services: ['Investment Consulting', 'Business Setup', 'Tax Planning', 'Due Diligence', 'Wealth Management'],
          keywords: 'consulting panama, panama investments, panama business, consultant panama'
        }
      },
      financial: {
        it: {
          name: 'Servizi Finanziari Internazionali Panama',
          description: 'Servizi finanziari specializzati per clienti internazionali a Panama. Gestione patrimoniale e pianificazione fiscale avanzata.',
          services: ['Gestione Patrimoniale', 'Pianificazione Fiscale', 'Servizi Bancari', 'Protezione Patrimoniale', 'Trust e Fondazioni'],
          keywords: 'servizi finanziari panama, gestione patrimonio panama, pianificazione fiscale panama'
        },
        es: {
          name: 'Servicios Financieros Internacionales Panamá',
          description: 'Servicios financieros especializados para clientes internacionales en Panamá. Gestión patrimonial y planificación fiscal avanzada.',
          services: ['Gestión Patrimonial', 'Planificación Fiscal', 'Servicios Bancarios', 'Protección Patrimonial', 'Trusts y Fundaciones'],
          keywords: 'servicios financieros panama, gestion patrimonio panama, planificacion fiscal panama'
        },
        en: {
          name: 'International Financial Services Panama',
          description: 'Specialized financial services for international clients in Panama. Wealth management and advanced tax planning.',
          services: ['Wealth Management', 'Tax Planning', 'Banking Services', 'Asset Protection', 'Trusts and Foundations'],
          keywords: 'financial services panama, wealth management panama, tax planning panama'
        }
      }
    };

    const locationData = locations[location as keyof typeof locations];
    const businessData = businessTypes[businessType as keyof typeof businessTypes][language as keyof typeof businessTypes.legal];

    return {
      location: locationData,
      business: businessData,
      language
    };
  };

  const updateGeoMetaTags = (localData: any) => {
    // Geographic meta tags
    updateMetaTag('name', 'geo.region', 'PA');
    updateMetaTag('name', 'geo.placename', localData.location.name);
    updateMetaTag('name', 'geo.position', `${localData.location.coordinates.lat};${localData.location.coordinates.lng}`);
    updateMetaTag('name', 'ICBM', `${localData.location.coordinates.lat}, ${localData.location.coordinates.lng}`);
    
    // Location-specific Open Graph tags
    updateMetaTag('property', 'og:locality', localData.location.name);
    updateMetaTag('property', 'og:region', 'Panama');
    updateMetaTag('property', 'og:country-name', 'Panama');
    updateMetaTag('property', 'og:postal-code', localData.location.postalCode);
    
    // Business-specific meta tags
    updateMetaTag('name', 'business:contact_data:locality', localData.location.name);
    updateMetaTag('name', 'business:contact_data:region', 'Panama');
    updateMetaTag('name', 'business:contact_data:country_name', 'Panama');
  };

  const updateLocalBusinessSchema = (localData: any) => {
    // Remove existing local business schema
    const existingSchema = document.querySelector('script[type="application/ld+json"][data-local-seo]');
    if (existingSchema) {
      existingSchema.remove();
    }

    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LegalService",
      "name": "RS International Services - Residencia Panama",
      "description": localData.business.description,
      "url": "https://residenciapanama.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://residenciapanama.com/logo.png",
        "width": 300,
        "height": 100
      },
      "image": [
        "https://residenciapanama.com/office-panama-city.jpg",
        "https://residenciapanama.com/panama-skyline.jpg",
        "https://residenciapanama.com/legal-services-panama.jpg"
      ],
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Avenida 10 Norte  Calle Tael Casa C1 Urbanizacion Los Angeles Panama City",
        "addressLocality": localData.location.name,
        "addressRegion": "Provincia de Panamá",
        "postalCode": localData.location.postalCode,
        "addressCountry": "PA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": localData.location.coordinates.lat,
        "longitude": localData.location.coordinates.lng
      },
      "telephone": "+507-6958-4333",
      "email": "info@residenciapanama.com",
      "faxNumber": "+507-6745-3211",
      "priceRange": "$$",
      "currenciesAccepted": ["USD", "EUR"],
      "paymentAccepted": ["Cash", "Credit Card", "Bank Transfer", "Wire Transfer"],
      "openingHours": [
        "Mo-Fr 09:00-18:00",
        "Sa 09:00-13:00"
      ],
      "areaServed": [
        {
          "@type": "Country",
          "name": "Panama"
        },
        {
          "@type": "Country", 
          "name": "Italy"
        },
        {
          "@type": "Country",
          "name": "Spain"
        }
      ],
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": localData.location.coordinates.lat,
          "longitude": localData.location.coordinates.lng
        },
        "geoRadius": "50000"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Panama Legal Services",
        "itemListElement": localData.business.services.map((service: string) => ({
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": service,
            "description": `Professional ${service.toLowerCase()} services in Panama`
          }
        }))
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "127",
        "bestRating": "5",
        "worstRating": "1"
      },
      "review": [
        {
          "@type": "Review",
          "author": {
            "@type": "Person",
            "name": "Marco Rossi"
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "reviewBody": "Servizio eccellente per la residenza a Panama. Professionali e competenti."
        }
      ],
      "sameAs": [
        "https://www.linkedin.com/company/rs-international-services",
        "https://www.facebook.com/residenciapanama",
        "https://twitter.com/residenciapanama"
      ],
      "knowsAbout": [
        "Panama Residency Law",
        "Offshore Company Formation",
        "International Tax Planning",
        "Real Estate Investment",
        "Banking Services"
      ],
      "memberOf": {
        "@type": "Organization",
        "name": "Colegio Nacional de Abogados de Panamá"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-local-seo', 'true');
    script.textContent = JSON.stringify(localBusinessSchema);
    document.head.appendChild(script);
  };

  const updateLocationKeywords = (localData: any) => {
    const locationKeywords = [
      `${localData.business.keywords}`,
      `${localData.location.name.toLowerCase()} panama`,
      `servizi ${localData.location.name.toLowerCase()}`,
      `ufficio ${localData.location.name.toLowerCase()}`,
      `consulenza ${localData.location.name.toLowerCase()}`,
      'panama city legal services',
      'residencia panama oficina',
      'servicios legales panama'
    ].join(', ');

    updateMetaTag('name', 'keywords', locationKeywords);
  };

  const updatePanamaHreflang = () => {
    // Panama-specific hreflang implementation
    const existingHreflang = document.querySelectorAll('link[rel="alternate"][hreflang*="PA"]');
    existingHreflang.forEach(tag => tag.remove());

    const panamaHreflang = document.createElement('link');
    panamaHreflang.setAttribute('rel', 'alternate');
    panamaHreflang.setAttribute('hreflang', 'es-PA');
    panamaHreflang.setAttribute('href', 'https://residenciapanama.com/es');
    document.head.appendChild(panamaHreflang);
  };

  const addBusinessVerificationTags = () => {
    // Google My Business verification
    updateMetaTag('name', 'google-site-verification', 'GOOGLE_VERIFICATION_CODE');
    
    // Bing Places verification
    updateMetaTag('name', 'msvalidate.01', 'BING_VERIFICATION_CODE');
    
    // Yandex verification (for international reach)
    updateMetaTag('name', 'yandex-verification', 'YANDEX_VERIFICATION_CODE');
  };

  const updateMetaTag = (attribute: string, name: string, content: string) => {
    if (!content) return;
    
    let meta = document.querySelector(`meta[${attribute}="${name}"]`);
    if (meta) {
      meta.setAttribute('content', content);
    } else {
      meta = document.createElement('meta');
      meta.setAttribute(attribute, name);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    }
  };

  // Component doesn't render anything visible
  return null;
};

export { LocalSEOPanama };
export default LocalSEOPanama;